// pages/social-login/pkceState.ts — client-side PKCE + anti-forgery `state`, and the sessionStorage
// attempt that survives the provider redirect. Pure logic (no React, no network) so the load-bearing
// anti-forgery check is unit-testable in the repo's node jest env.
//
// Unlike the casino front (where the PHP proxy kept the verifier server-side against a relay), owner-hub
// is a public client: the FE generates the `code_verifier`, sends only the `code_challenge` to the
// provider, and posts the verifier to OUR backend on the callback. There is no relay and no server state
// store, so `state` anti-forgery lives entirely here: fail closed on a missing/mismatched state.

import type { SsoProvider } from './config';

/** sessionStorage key holding the in-flight SSO attempt across the provider redirect. */
export const SSO_ATTEMPT_KEY = 'nova.sso.attempt';

/** Callback query-param names the provider returns on the OIDC code flow. */
export const CALLBACK_CODE_PARAM = 'code';
export const CALLBACK_STATE_PARAM = 'state';
export const CALLBACK_ERROR_PARAM = 'error';

/** Whether the redirect was started to LOG IN (default) or to LINK the provider to the already
 *  signed-in account (the settings "Connect" flow). The callback branches on this. */
export type SsoAttemptMode = 'login' | 'link';

/** The in-flight attempt persisted before the redirect and read back on the callback. */
export interface SsoAttempt {
  provider: SsoProvider;
  /** 'login' (default) or 'link' (connect-from-settings). Absent is treated as 'login'. */
  mode?: SsoAttemptMode;
  /** Anti-forgery value placed on the authorize URL; must match the callback's `state`. */
  state: string;
  /** PKCE verifier; posted to our backend with the code, never to the provider. */
  verifier: string;
  /** The `redirect_uri` used on the authorize request; reused verbatim on the exchange. */
  redirectUri: string;
  /** In-app route to restore after a successful round-trip (optional). */
  returnTo?: string;
  /**
   * The visitor's onboarding session id, captured BEFORE the pre-sign-in logout tore it down, so the
   * callback can bind it on the account (see authentication.ts `withSsoLoginOptions`). Carried here
   * because it cannot be read from the store/localStorage after the redirect.
   */
  sessionId?: string;
}

function webcrypto(): Crypto {
  const crypto = (globalThis as { crypto?: Crypto }).crypto;
  if (!crypto || !crypto.getRandomValues) {
    throw new Error('WebCrypto is not available in this environment');
  }
  return crypto;
}

/** base64url (RFC 4648 §5) with no padding, from raw bytes. */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** `n` cryptographically-random bytes. */
function randomBytes(n: number): Uint8Array {
  const bytes = new Uint8Array(n);
  webcrypto().getRandomValues(bytes);
  return bytes;
}

/**
 * A PKCE `code_verifier`: 32 random bytes as base64url => a 43-char high-entropy string, within the
 * RFC 7636 43-128 range.
 */
export function generateVerifier(): string {
  return base64UrlEncode(randomBytes(32));
}

/** The S256 `code_challenge` for a verifier: base64url(SHA-256(ASCII(verifier))). */
export async function challengeFromVerifier(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await webcrypto().subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** A random anti-forgery `state` (16 random bytes as base64url). */
export function mintState(): string {
  return base64UrlEncode(randomBytes(16));
}

export type CallbackStatus = 'none' | 'error' | 'mismatch' | 'ready';

export interface CallbackDecision {
  status: CallbackStatus;
  code?: string;
  state?: string;
  /** The provider's error code, on `status: 'error'`. */
  error?: string;
}

/**
 * Decide what to do with a callback URL, WITHOUT any network call.
 *   - `none`     => not a callback (no code, no error) => no-op.
 *   - `error`    => the provider refused (e.g. `error=access_denied`) => report, do not exchange.
 *   - `mismatch` => code present but `state` missing or != the stored attempt => abort, NEVER exchange.
 *   - `ready`    => validated; safe to post the code.
 *
 * Fail-closed on an empty state: a present-but-empty `?state=` reads back as `''`, and a stored `''`
 * would let `'' === ''` through, so a code with no usable state is a `mismatch`, never `ready`. This is
 * the only anti-forgery guard (no server state store), so it must not degrade to "an attempt exists".
 */
export function evaluateCallback(search: string, stored: SsoAttempt | null): CallbackDecision {
  const params = new URLSearchParams(search);
  const error = params.get(CALLBACK_ERROR_PARAM);
  if (error) {
    return { status: 'error', error };
  }

  const code = params.get(CALLBACK_CODE_PARAM);
  const state = params.get(CALLBACK_STATE_PARAM);
  if (!code) {
    return { status: 'none' };
  }
  if (!state || !stored || !stored.state || stored.state !== state) {
    return { status: 'mismatch' };
  }
  return { status: 'ready', code, state };
}

/** Remove the SSO callback params from a search string, preserving any others. */
export function stripCallbackParams(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(CALLBACK_CODE_PARAM);
  params.delete(CALLBACK_STATE_PARAM);
  params.delete(CALLBACK_ERROR_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

// --- sessionStorage access (guarded for the node/SSR case) ------------------------------------------

export function readSsoAttempt(): SsoAttempt | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(SSO_ATTEMPT_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as SsoAttempt;
    return parsed.provider && parsed.state && parsed.verifier ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSsoAttempt(attempt: SsoAttempt): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(SSO_ATTEMPT_KEY, JSON.stringify(attempt));
}

export function clearSsoAttempt(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(SSO_ATTEMPT_KEY);
}

// --- link-mode one-shot error handoff -----------------------------------------------------------
// A link ('Connect from settings') round-trip finishes on `/telegram-callback`, which then navigates
// back to settings. It has no shared component with SsoConnections to hand a failure to, so it drops
// a one-shot code in sessionStorage that SsoConnections reads and clears on mount. Kept out of the URL
// so a failure code never lands in history / a shareable link.

/** sessionStorage key holding a one-shot link-mode failure code for SsoConnections to render. */
export const SSO_LINK_ERROR_KEY = 'nova.sso.linkError';

/** `already` = identity linked to another account; `expired` = missing/expired auth token (401);
 *  `failed` = any other link failure (network, backend). */
export type SsoLinkError = 'already' | 'expired' | 'failed';

export function writeLinkError(err: SsoLinkError): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.setItem(SSO_LINK_ERROR_KEY, err);
}

/** Read AND remove the one-shot link error (so it shows exactly once after the redirect). */
export function takeLinkError(): SsoLinkError | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const raw = sessionStorage.getItem(SSO_LINK_ERROR_KEY);
  if (raw === 'already' || raw === 'expired' || raw === 'failed') {
    sessionStorage.removeItem(SSO_LINK_ERROR_KEY);
    return raw;
  }
  if (raw !== null) {
    sessionStorage.removeItem(SSO_LINK_ERROR_KEY);
  }
  return null;
}
